import { useTheme } from '@mui/material';
import { useGiftContext } from '../Context/GiftContextProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useUploadGiftSenderImage from './useUploadGiftSenderImage';
import useUploadGiftPendulumLogo from './useUploadGiftPendulumLogo';
import { Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import klydoApiClient from '../../../api/klydo';
import { GiftKlydo } from '../../../api/gift';
import { PendulumLogoType } from '../../../consts/pendulumLogo';
import { DEFAULT_PHOTO, MAX_GREETING_LENGTH } from '../consts';

const useAdminGiftForm = () => {
  const defaultPhoto = DEFAULT_PHOTO;
  const theme = useTheme();
  const {
    setKlydoGiftProps,
    setSelectedKlydoGreet,
    klydoGiftProps,
    customKlydoId,
    setCustomKlydoId,
    resetSenderImageUrl,
  } = useGiftContext();

  const { data: customKlydo } = useQuery({
    queryKey: ['gift-custom-klydo', customKlydoId],
    queryFn: () => klydoApiClient.getKlydo(customKlydoId!),
    enabled: !!customKlydoId,
  });

  const [greeting, setGreeting] = useState(
    klydoGiftProps?.greetingContent || '',
  );
  const [name, setName] = useState(klydoGiftProps?.senderName || '');
  const [selectedOption, setSelectedOption] = useState(
    klydoGiftProps?.pendulumLogoUrl ? 'logo' : 'text',
  );
  const [logoFull, setLogoFull] = useState(
    klydoGiftProps?.pendulumLogoType === 'full',
  );
  const [profileUrl, setProfileUrl] = useState(
    klydoGiftProps?.senderImageUrl || defaultPhoto,
  );
  const [pendulumLogoUrl, setPendulumLogoUrl] = useState(
    klydoGiftProps?.pendulumLogoUrl || '',
  );
  const [localCustomKlydoId, setLocalCustomKlydoId] = useState(
    klydoGiftProps?.klydoId || '',
  );
  const [profileImageOption, setProfileImageOption] = useState('defualtPhoto');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const { handleFileSelection: handleProfileUpload } = useUploadGiftSenderImage(
    {
      setUrl: setProfileUrl,
      onSuccess: (url) => setKlydoGiftProps({ senderImageUrl: url }),
      onError: () => {
        setProfileUrl(defaultPhoto);
        resetSenderImageUrl();
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    },
  );

  const { handleFileSelection: handleLogoUpload } = useUploadGiftPendulumLogo({
    setUrl: setPendulumLogoUrl,
    pendulumLogoType: logoFull ? 'full' : 'sized',
    onSuccess: (url) => setKlydoGiftProps({ pendulumLogoUrl: url }),
    onError: (message) => alert(message || 'Failed to upload pendulum logo'),
  });

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
    // A logo gift draws no name, so a name typed earlier must not be saved.
    if (event.target.value === 'logo') {
      setName('');
      setKlydoGiftProps({ senderName: '' });
    }
  };

  // The uploaded image is baked for one type, so changing the type drops it.
  const handleLogoFullChange = (full: boolean) => {
    const pendulumLogoType: PendulumLogoType = full ? 'full' : 'sized';
    setLogoFull(full);
    setPendulumLogoUrl('');
    setKlydoGiftProps({ pendulumLogoUrl: '', pendulumLogoType });
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleProfileClick = () => fileInputRef.current?.click();
  const handleLogoClick = () => logoInputRef.current?.click();

  const handleCustomKlydo = (klydoId: string) => {
    setLocalCustomKlydoId(klydoId);
    setCustomKlydoId(klydoId);
    setKlydoGiftProps({ ...klydoGiftProps, klydoId });
  };

  const handleGreetChange = (greet: string) => {
    if (greet.length > MAX_GREETING_LENGTH) return;
    setGreeting(greet);
    setKlydoGiftProps({ greetingContent: greet });
  };

  const handleNameChange = (senderName: string) => {
    if (senderName.length > 14) return;
    setName(senderName);
    setKlydoGiftProps({ senderName });
  };

  const handleDateChange = (selectedDate: Dayjs | null) => {
    setKlydoGiftProps({ date: selectedDate?.valueOf() ?? undefined });
  };

  const handleRemoveProfile = () => {
    setProfileUrl(defaultPhoto);
    resetSenderImageUrl();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveLogo = () => {
    setPendulumLogoUrl('');
    setKlydoGiftProps({ pendulumLogoUrl: '' });
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const linesLimit = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const lines = greeting.split('\n');
      if (event.key === 'Enter' && lines.length >= 8) event.preventDefault();
    },
    [greeting],
  );

  useEffect(() => {
    if (customKlydo) {
      const giftKlydo: GiftKlydo = {
        id: customKlydo.id,
        loopUrl: customKlydo.loopUrl,
        theme: customKlydo.theme,
        klydoAnimation: customKlydo.klydoAnimation,
        photoUrl: customKlydo.photoUrl,
        name: customKlydo.name,
      };
      setSelectedKlydoGreet(giftKlydo);
    }
  }, [customKlydo]);

  return {
    theme,
    greeting,
    name,
    selectedOption,
    profileUrl,
    pendulumLogoUrl,
    logoFull,
    fileInputRef,
    logoInputRef,
    localCustomKlydoId,
    klydoGiftProps,
    defaultPhoto,
    profileImageOption,
    setProfileImageOption,
    handleProfileClick,
    handleLogoClick,
    handleCustomKlydo,
    handleOptionChange,
    handleLogoFullChange,
    handleGreetChange,
    handleNameChange,
    handleDateChange,
    handleRemoveProfile,
    handleRemoveLogo,
    handlePickProfile: handleProfileUpload,
    handlePickLogo: handleLogoUpload,
    linesLimit,
  };
};

export default useAdminGiftForm;
